/**
 * Pure H OS · Pre-Production Gap Closure · Budget vs Actual.
 * Variance drill-down per Cost Center. Derived live from COST_CENTERS.
 */
import { useMemo } from "react";
import { Card, SectionHeader, SeverityBadge } from "../../components/lib";
import { DocumentActions } from "../../components/DocumentActions";
import { COST_CENTERS } from "../../mocks/costCentersData";

function severity(pct: number): "low" | "medium" | "high" {
  if (pct >= 95) return "high";
  if (pct >= 80) return "medium";
  return "low";
}

export default function BudgetVsActual() {
  const rows = useMemo(() => {
    return COST_CENTERS.map((c) => {
      const budget = Number(c.budgetEGP) || 0;
      const spent  = Number(c.spentEGP)  || 0;
      const variance = budget - spent;
      const variancePct = budget > 0 ? (variance / budget) * 100 : 0;
      const utilizationPct = Number(c.utilizationPct) || 0;
      return { ...c, budgetEGP: budget, spentEGP: spent, variance, variancePct, utilizationPct };
    });
  }, []);

  const totalBudget = rows.reduce((s, r) => s + r.budgetEGP, 0);
  const totalSpent  = rows.reduce((s, r) => s + r.spentEGP, 0);
  const totalVar    = totalBudget - totalSpent;

  return (
    <div className="space-y-6" data-testid="bva-page">
      <Card testId="bva-summary">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <SectionHeader eyebrow="Budget vs Actual · variance drill-down" title={`${rows.length} cost centers · ${rows[0]?.month ?? ""}`} />
          <DocumentActions docKey="budget-vs-actual" testId="bva-doc-actions" />
        </div>
        <div className="grid grid-cols-3 gap-4 mt-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.16em]" style={{ color: "var(--ph-muted)" }}>Total budget</div>
            <div className="mt-1 text-lg font-semibold tabular-nums" style={{ color: "var(--ph-text)" }}>EGP {totalBudget.toLocaleString()}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.16em]" style={{ color: "var(--ph-muted)" }}>Total spent</div>
            <div className="mt-1 text-lg font-semibold tabular-nums" style={{ color: "var(--ph-text)" }}>EGP {totalSpent.toLocaleString()}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.16em]" style={{ color: "var(--ph-muted)" }}>Variance</div>
            <div className="mt-1 text-lg font-semibold tabular-nums" style={{ color: totalVar >= 0 ? "var(--ph-accent)" : "var(--ph-danger)" }}>
              {totalVar >= 0 ? "+" : ""}EGP {totalVar.toLocaleString()}
            </div>
          </div>
        </div>
      </Card>

      <Card padded={false} testId="bva-table">
        <div className="grid grid-cols-8 text-[10px] uppercase tracking-[0.14em] px-4 py-3" style={{ background: "var(--ph-surface-subtle)", color: "var(--ph-muted)" }}>
          <div className="col-span-2">Cost Center</div>
          <div>Owner</div>
          <div className="text-right">Budget</div>
          <div className="text-right">Spent</div>
          <div className="text-right">Variance</div>
          <div className="text-right">Variance %</div>
          <div>Status</div>
        </div>
        {rows.map((r) => (
          <div key={r.id} data-testid={`bva-row-${r.id}`} className="grid grid-cols-8 text-sm px-4 py-3 items-center" style={{ borderTop: "1px solid var(--ph-border-subtle)" }}>
            <div className="col-span-2">
              <div className="text-[10px] uppercase tracking-[0.14em]" style={{ color: "var(--ph-muted)" }}>{r.code}</div>
              <div style={{ color: "var(--ph-text)" }}>{r.name}</div>
            </div>
            <div style={{ color: "var(--ph-text)" }}>{r.owner}</div>
            <div className="text-right tabular-nums" style={{ color: "var(--ph-text)" }}>EGP {r.budgetEGP.toLocaleString()}</div>
            <div className="text-right tabular-nums" style={{ color: "var(--ph-text)" }}>EGP {r.spentEGP.toLocaleString()}</div>
            <div className="text-right tabular-nums" style={{ color: r.variance >= 0 ? "var(--ph-accent)" : "var(--ph-danger)" }}>
              {r.variance >= 0 ? "+" : ""}EGP {r.variance.toLocaleString()}
            </div>
            <div className="text-right tabular-nums" style={{ color: "var(--ph-text)" }}>{r.variancePct.toFixed(1)}%</div>
            <div><SeverityBadge severity={severity(r.utilizationPct)} /></div>
          </div>
        ))}
      </Card>
    </div>
  );
}
